import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, View, ViewStyle } from 'react-native';
import { bottomBarHeight, fullWidth, scale as initialScale, webHeader } from '@components/frame';
import { isWeb } from '@helpers';
import { SIDE_PANEL_ANIMATION_DURATION_MS, SIDE_PANEL_Z_INDEX } from '@v2/pages/common/components/SidePanel/constants';
import type { SidePanelProps } from '@v2/pages/common/components/SidePanel';

export type UseManageSidePanelParams = Pick<
  SidePanelProps,
  'isOpen' | 'onClose' | 'width' | 'children' | 'topOffset' | 'bottomOffset' | 'animationDuration' | 'zIndex' | 'scale' | 'closeOnEscape'
>;

export interface UseManageSidePanelResult {
  isMounted: boolean;
  panelRef: RefObject<View>;
  renderedChildren: ReactNode;
  style: Animated.WithAnimatedValue<ViewStyle>;
}

export const useManageSidePanel = ({
  isOpen,
  onClose,
  width,
  children,
  topOffset,
  bottomOffset,
  animationDuration = SIDE_PANEL_ANIMATION_DURATION_MS,
  zIndex = SIDE_PANEL_Z_INDEX,
  scale = initialScale,
  closeOnEscape = true,
}: UseManageSidePanelParams): UseManageSidePanelResult => {
  const [isMounted, setIsMounted] = useState<boolean>(isOpen);
  const translateX = useRef(new Animated.Value(isOpen ? 0 : fullWidth)).current;
  const panelRef = useRef<View>(null);

  const isOpenRef = useRef<boolean>(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const lastChildrenRef = useRef<ReactNode>(null);
  if (children) lastChildrenRef.current = children;
  const renderedChildren = children ?? lastChildrenRef.current;

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      Animated.timing(translateX, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: true,
      }).start();
      return;
    }

    Animated.timing(translateX, {
      toValue: fullWidth,
      duration: animationDuration,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished && !isOpenRef.current) setIsMounted(false);
    });
  }, [isOpen, animationDuration, translateX]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape || !isWeb || typeof document === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  useEffect(() => {
    if (!isOpen || !isWeb || typeof document === 'undefined') return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const panel = panelRef.current as unknown as HTMLElement | null;
      if (panel instanceof Element && panel.contains(target)) return;
      onClose();
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [isOpen, onClose]);

  const style = useMemo<Animated.WithAnimatedValue<ViewStyle>>(
    () => ({
      position: 'absolute',
      right: 0,
      top: topOffset ?? (isWeb ? webHeader : 0),
      bottom: bottomOffset ?? bottomBarHeight,
      width: scale(width),
      transform: [{ translateX }],
      zIndex,
    }),
    [topOffset, bottomOffset, width, scale, translateX, zIndex]
  );

  return { isMounted, panelRef, renderedChildren, style };
};
